import {faDownload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Meta} from '@rdcomponents/meta/meta';
import {PageCardDivider} from '@rdcomponents/page-card-divider/page-card-divider';
import {PageHeaderTitle} from '@rdcomponents/page-header-title/page-header-title';
import {UsetifulNameProductEnum} from '@rdcomponents/usetiful/usetiful-const';
import {Button, Checkbox, Popover} from 'antd';
import type {CheckboxChangeEvent} from 'antd/es/checkbox';
import {useCallback, useMemo, useState} from 'react';

import {appRoute} from '../../../../../app-route';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-card';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../../../service/analytics/analytics';
import {
    useCompanyRatingDynamics,
    useRequestRatingsDynamicsExport,
} from '../../../../../service/reivew-analysis/rating-dynamics/reviews-analytics-ratings-dynamic-hook';

import {RatingHistoryFilter} from './rating-history-filter/rating-history-filter';
import {RatingHistoryTable} from './rating-history-table/rating-history-table';
import {RatingHistoryAverageCatalogRatingCard} from './statistics-card/rating-history-average-catalog-rating-card';
import {RatingHistoryAverageOverallRatingCard} from './statistics-card/rating-history-average-rating-card';
import * as styles from './rating-history-page.scss';

export function RatingHistoryPage(): JSX.Element {
    const {filter, updateFilter, updateSelectedDates, updateDatesPreset, query, pagination} =
        useCompanyRatingDynamics();

    const {mutateAsync, isLoading: isRequestingReport} = useRequestRatingsDynamicsExport(filter);

    const {data, isLoading} = query || {};
    const {getLocalizedString} = useLocale();

    const [isWithAspects, setIsWithAspects] = useState<boolean>(false);
    const [isDownloadPopoverOpen, setIsDownloadPopoverOpen] = useState<boolean>(false);

    const handleDownloadClick = useCallback(() => {
        track(AnalyticsTarget.ReviewsAnalysis.RequestReport, 'xlsx');

        mutateAsync({withAspects: isWithAspects})
            .then(() => {
                setIsDownloadPopoverOpen(false);
            })
            .catch(() => {
                setIsDownloadPopoverOpen(true);
            });
    }, [isWithAspects, mutateAsync]);

    const handleWithAspectsChange = useCallback((event: CheckboxChangeEvent) => {
        setIsWithAspects(event.target.checked);
    }, []);

    const handleDownloadPopoverOpenChange = useCallback((open: boolean) => {
        setIsDownloadPopoverOpen(open);
    }, []);

    const downloadPopoverContent = useMemo(() => {
        return (
            <div className={styles.RatingHistoryPage_downloadPopover}>
                <Checkbox checked={isWithAspects} disabled={isRequestingReport} onChange={handleWithAspectsChange}>
                    <Locale stringKey="REVIEWS_ANALYSIS__RATINGS_DYNAMICS__REPORT__INCLUDE_ASPECTS" />
                </Checkbox>
                <Button
                    block
                    disabled={isRequestingReport}
                    icon={<FontAwesomeIcon icon={faDownload} />}
                    loading={isRequestingReport}
                    onClick={handleDownloadClick}
                >
                    <Locale stringKey="REVIEWS_ANALYSIS__RATINGS_DYNAMICS__REPORT__DOWNLOAD_REPORT" />
                </Button>
            </div>
        );
    }, [handleDownloadClick, handleWithAspectsChange, isRequestingReport, isWithAspects]);

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <PageCard>
                <Meta title={getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__RATINGS')} />

                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.reviewsGeneratorQrCode.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS',
                        },
                        {
                            path: appRoute.reviewAnalysisGeneratorRatings.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS__RATINGS',
                        },
                    ]}
                />

                <PageHeader className={styles.RatingHistoryPage_header}>
                    <PageHeaderTitle
                        productName={UsetifulNameProductEnum.REVIEWS_ANALYSIS__CARD_RATING}
                        title="CATEGORY_NAME__REVIEWS_ANALYSIS__RATINGS"
                    />
                    <Popover
                        content={downloadPopoverContent}
                        onOpenChange={handleDownloadPopoverOpenChange}
                        open={isDownloadPopoverOpen}
                        placement="bottomRight"
                        trigger="click"
                    >
                        <Button
                            className={styles.RatingHistoryPage_downloadButton}
                            disabled={isRequestingReport}
                            loading={isRequestingReport}
                        >
                            <Locale stringKey="REVIEWS_ANALYSIS__RATINGS_DYNAMICS__REPORT__REQUEST" />
                        </Button>
                    </Popover>
                </PageHeader>

                <PageCardDivider />

                <RatingHistoryFilter
                    filter={filter}
                    updateDatesPreset={updateDatesPreset}
                    updateSelectedDates={updateSelectedDates}
                />
            </PageCard>

            <div className={styles.RatingHistoryPage_averageRatingCardsContainer}>
                {isLoading ? (
                    <>
                        <RatingHistoryAverageOverallRatingCard loading />
                        <RatingHistoryAverageCatalogRatingCard loading />
                        <RatingHistoryAverageCatalogRatingCard loading />
                    </>
                ) : (
                    <>
                        {data?.ratingsAverage?.overallAverage && (
                            <RatingHistoryAverageOverallRatingCard ratingData={data?.ratingsAverage?.overallAverage} />
                        )}

                        {data?.ratingsAverage?.catalogAverages?.map((catalogRating) => {
                            return (
                                <RatingHistoryAverageCatalogRatingCard
                                    catalogRatingData={catalogRating}
                                    key={catalogRating.catalog}
                                />
                            );
                        })}
                    </>
                )}
            </div>

            <PageCard>
                <RatingHistoryTable
                    data={data?.results || []}
                    filter={filter}
                    isFetchingData={isLoading}
                    pagination={pagination}
                    updateFilter={updateFilter}
                />
            </PageCard>
        </Page>
    );
}
